import * as React from 'react';

import { cn } from '@/lib/utils';

export type CardProps = React.ComponentProps<'div'> & {
  spacing?: 'sm' | 'md';
};

function Card({ spacing = 'md', className, style, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        'flex flex-col gap-8 rounded-md border bg-card text-card-foreground shadow-sm sm:max-w-[860px] sm:gap-12',
        spacing == 'sm' && 'px-4 py-6 sm:px-10 sm:py-7',
        spacing == 'md' && 'px-5 py-8 sm:px-15 sm:py-11.5',
        className
      )}
      style={{
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        ...style,
      }}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start',
        'has-data-[slot=card-action]:grid-cols-[1fr_auto]',
        '[.border-b]:pb-6',
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'font-martina text-[32px] leading-[115%] font-normal tracking-tight sm:text-[48px]',
        className
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('mt-8 border-l-2 border-orchid-500 pl-4 font-circular text-sm', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-4 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
